<template>

  <div class="poll-page">

    <section class="poll-content">
    <q-breadcrumbs
      active-color="blue-10"
      separator-color="blue-10"
      class="text-blue-grey-10"
    >
      <q-breadcrumbs-el class="cursor-pointer" label="Главная" icon="home" @click="goToMain" />
      <q-breadcrumbs-el label="Анкетирование" icon="ballot" />
    </q-breadcrumbs>

      <!-- MOBILE -->
      <q-stepper
        v-if="isMobile"
        v-model="step"
        vertical
        animated
        header-nav
        color="accent"
        active-color="blue-grey-8"
        done-color="light-green-6"
        keep-alive
      >

        <q-step
          v-for="chapter in poll"
          :key="chapter.chapter_number"
          :name="chapter.chapter_number"
          :title="chapter.chapter"
          :caption="chapter.description"
          icon="ballot"
          :done="step > chapter.chapter_number"
        >

          <q-list>

            <q-item
              v-for="question in chapter.questions"
              :key="question.number"
            >

              <Suspense>

              <component
                :is="defineFieldType(question.type)"
                :context="question"
                @hoistAnswer="captureAnswer"
              />

              <template #fallback>
                <AppLoader />
              </template>

              </Suspense>

            </q-item>

          </q-list>

          <q-stepper-navigation>

            <q-btn
              v-if="chapter.chapter_number > 1"
              flat
              color="primary"
              icon="arrow_back"
              label="Назад"
              @click="prevStep(chapter)"
            />

            <q-btn
              v-if="chapter.chapter_number < totalSteps"
              color="accent"
              icon="arrow_forward"
              label="Далее"
              @click="nextStep(chapter)"
            />

            <div
              v-if="chapter.chapter_number === totalSteps"
              class="gu-actions-right"
            >

              <q-btn
                unelevated
                no-caps
                :ripple="false"
                class="gu-btn gu-btn-secondary"
                label="Продолжить позже"
                @click="continueLater"
              />

              <q-btn
                unelevated
                no-caps
                :ripple="false"
                class="gu-btn gu-btn-primary"
                label="Завершить"
                @click="openFinishConfirm"
              />

            </div>

          </q-stepper-navigation>

        </q-step>

        <q-step
          v-if="hasDesigns"
          :name="designStepNumber"
          title="Назначения"
          caption="Ознакомьтесь со списком назначений"
          icon="assignment"
          :done="step > designStepNumber"
        >

          <div class="designs-note">
            <q-icon name="info" size="18px" color="primary" />
            <span>Если Вы недавно сдавали анализы или были у специалиста,
              упомянутого в списке назначений, можете заранее отказаться от этих услуг</span>
          </div>

          <div class="designs-list">

            <div
              v-for="design in regularDesigns"
              :key="design.iddesign"
              class="design-card"
              :class="{ 'design-card--canceled': design.canceled }"
            >

              <div class="design-name">
                {{ design.short_name }}
              </div>

              <q-btn
                unelevated
                no-caps
                :ripple="false"
                :icon="design.canceled ? 'add' : 'close'"
                :loading="processingDesignIds.includes(design.iddesign)"
                :disable="processingDesignIds.includes(design.iddesign)"
                class="gu-btn gu-btn-secondary gu-btn-sm"
                :label="design.canceled ? 'Вернуть' : 'Отказаться'"
                @click="toggleDesign(design)"
              />

            </div>

            <div v-if="labDesigns.length" class="design-group">

              <div class="design-group-header">

                <div class="design-group-title">
                  Лабораторные назначения
                </div>

                <q-btn
                  unelevated
                  no-caps
                  :ripple="false"
                  icon="close"
                  :loading="cancelingAllLab"
                  :disable="cancelingAllLab"
                  class="gu-btn gu-btn-secondary gu-btn-sm"
                  label="Отказаться от всех лабораторных назначений"
                  @click="cancelAllLabDesigns"
                />

              </div>

              <div
                v-for="design in labDesigns"
                :key="design.iddesign"
                class="design-card"
                :class="{ 'design-card--canceled': design.canceled }"
              >

                <div class="design-name">
                  {{ design.short_name }}
                </div>

                <q-btn
                  unelevated
                  no-caps
                  :ripple="false"
                  :icon="design.canceled ? 'add' : 'close'"
                  :loading="processingDesignIds.includes(design.iddesign)"
                  :disable="processingDesignIds.includes(design.iddesign)"
                  class="gu-btn gu-btn-secondary gu-btn-sm"
                  :label="design.canceled ? 'Вернуть' : 'Отказаться'"
                  @click="toggleDesign(design)"
                />

              </div>

            </div>

          </div>

          <q-stepper-navigation>

            <q-btn
              v-if="designStepNumber > 1"
              flat
              color="primary"
              icon="arrow_back"
              label="Назад"
              @click="step -= 1"
            />

            <div class="gu-actions-right">

              <q-btn
                unelevated
                no-caps
                :ripple="false"
                class="gu-btn gu-btn-secondary"
                label="Продолжить позже"
                @click="continueLater"
              />

              <q-btn
                unelevated
                no-caps
                :ripple="false"
                class="gu-btn gu-btn-primary"
                label="Завершить"
                @click="openFinishConfirm"
              />

            </div>

          </q-stepper-navigation>

        </q-step>

      </q-stepper>

      <!-- DESKTOP -->
      <div v-else class="poll-layout">

        <aside class="poll-sidebar">

          <div
            v-for="chapter in poll"
            :key="chapter.chapter_number"
            class="chapter-item"
            :class="{ active: step === chapter.chapter_number }"
            @click="step = chapter.chapter_number"
          >

            <div class="chapter-number">
              {{ chapter.chapter_number }}
            </div>

            <div>

              <div class="chapter-title">
                {{ chapter.chapter }}
              </div>

              <div class="chapter-subtitle">
                {{ chapter.description }}
              </div>

            </div>

          </div>

          <div
            v-if="hasDesigns"
            class="chapter-item"
            :class="{ active: step === designStepNumber }"
            @click="step = designStepNumber"
          >

            <div class="chapter-number">
              {{ designStepNumber }}
            </div>

            <div>

              <div class="chapter-title">
                Назначения
              </div>

              <div class="chapter-subtitle">
                Ознакомьтесь со списком назначений
              </div>

            </div>

          </div>

        </aside>

        <main class="poll-main">

          <div
            v-for="chapter in poll"
            :key="chapter.chapter_number"
            v-show="chapter.chapter_number === step"
          >

            <div class="chapter-header">
              {{ chapter.chapter }}
            </div>

            <div class="question-list">

              <div
                v-for="question in chapter.questions"
                :key="question.number"
                class="question-card"
              >

                <Suspense>

                <component
                  :is="defineFieldType(question.type)"
                  :context="question"
                  @hoistAnswer="captureAnswer"
                />

                <template #fallback>
                  <AppLoader />
                </template>

                </Suspense>

              </div>

            </div>

            <div class="poll-nav">

              <q-btn
                v-if="chapter.chapter_number > 1"
                flat
                icon="arrow_back"
                color="primary"
                label="Назад"
                @click="prevStep(chapter)"
              />

              <q-btn
                v-if="chapter.chapter_number < totalSteps"
                color="accent"
                icon="arrow_forward"
                label="Далее"
                @click="nextStep(chapter)"
              />

              <div
                v-if="chapter.chapter_number === totalSteps"
                class="gu-actions-right"
              >

                <q-btn
                  unelevated
                  no-caps
                  :ripple="false"
                  class="gu-btn gu-btn-secondary"
                  label="Продолжить позже"
                  @click="continueLater"
                />

                <q-btn
                  unelevated
                  no-caps
                  :ripple="false"
                  class="gu-btn gu-btn-primary"
                  label="Завершить"
                  @click="openFinishConfirm"
                />

              </div>

            </div>

          </div>

          <div v-if="hasDesigns" v-show="step === designStepNumber">

            <div class="chapter-header">
              Назначения
            </div>

            <div class="designs-note">
              <q-icon name="info" size="18px" color="primary" />
              <span>Если Вы недавно сдавали анализы или были у специалиста,
                упомянутого в списке назначений, можете заранее отказаться от этих услуг</span>
            </div>

            <div class="designs-list">

              <div
                v-for="design in regularDesigns"
                :key="design.iddesign"
                class="design-card"
                :class="{ 'design-card--canceled': design.canceled }"
              >

                <div class="design-name">
                  {{ design.short_name }}
                </div>

                <q-btn
                  unelevated
                  no-caps
                  :ripple="false"
                  :icon="design.canceled ? 'add' : 'close'"
                  :loading="processingDesignIds.includes(design.iddesign)"
                  :disable="processingDesignIds.includes(design.iddesign)"
                  class="gu-btn gu-btn-secondary gu-btn-sm"
                  :label="design.canceled ? 'Вернуть' : 'Отказаться'"
                  @click="toggleDesign(design)"
                />

              </div>

              <div v-if="labDesigns.length" class="design-group">

                <div class="design-group-header">

                  <div class="design-group-title">
                    Лабораторные назначения
                  </div>

                  <q-btn
                    unelevated
                    no-caps
                    :ripple="false"
                    icon="close"
                    :loading="cancelingAllLab"
                    :disable="cancelingAllLab"
                    class="gu-btn gu-btn-secondary gu-btn-sm"
                    label="Отказаться от всех лабораторных назначений"
                    @click="cancelAllLabDesigns"
                  />

                </div>

                <div
                  v-for="design in labDesigns"
                  :key="design.iddesign"
                  class="design-card"
                  :class="{ 'design-card--canceled': design.canceled }"
                >

                  <div class="design-name">
                    {{ design.short_name }}
                  </div>

                  <q-btn
                    unelevated
                    no-caps
                    :ripple="false"
                    :icon="design.canceled ? 'add' : 'close'"
                    :loading="processingDesignIds.includes(design.iddesign)"
                    :disable="processingDesignIds.includes(design.iddesign)"
                    class="gu-btn gu-btn-secondary gu-btn-sm"
                    :label="design.canceled ? 'Вернуть' : 'Отказаться'"
                    @click="toggleDesign(design)"
                  />

                </div>

              </div>

            </div>

            <div class="poll-nav">

              <q-btn
                v-if="designStepNumber > 1"
                flat
                icon="arrow_back"
                color="primary"
                label="Назад"
                @click="step -= 1"
              />

              <div class="gu-actions-right">

                <q-btn
                  unelevated
                  no-caps
                  :ripple="false"
                  class="gu-btn gu-btn-secondary"
                  label="Продолжить позже"
                  @click="continueLater"
                />

                <q-btn
                  unelevated
                  no-caps
                  :ripple="false"
                  class="gu-btn gu-btn-primary"
                  label="Завершить"
                  @click="openFinishConfirm"
                />

              </div>

            </div>

          </div>

        </main>

      </div>

    </section>

    <q-dialog v-model="showFinishConfirm" persistent>

      <q-card class="gu-confirm-card">

        <div class="gu-confirm-icon">
          <q-icon name="description" size="26px" />
        </div>

        <div class="gu-confirm-title">
          Завершение анкетирования
        </div>

        <div class="gu-confirm-text">
          По завершению будет создан документ на подпись. Уверены, что ответили на все вопросы?
        </div>

        <div class="gu-confirm-actions">

          <q-btn
            unelevated
            no-caps
            :ripple="false"
            :disable="creatingPollFile"
            class="gu-btn gu-btn-secondary"
            label="Назад к вопросам"
            @click="showFinishConfirm = false"
          />

          <q-btn
            unelevated
            no-caps
            :ripple="false"
            :loading="creatingPollFile"
            :disable="creatingPollFile"
            class="gu-btn gu-btn-primary"
            label="Подтвердить"
            @click="confirmFinishPoll"
          />

        </div>

      </q-card>

    </q-dialog>

  </div>

</template>

<script lang="ts">
import {
  ref,
  computed,
  defineAsyncComponent,
  onMounted,
} from 'vue';
import { storeToRefs } from 'pinia';
// eslint-disable-next-line import/no-cycle
import { useEcoHospitalStore } from '~store/ecohospital/ecohospitalModule';
// eslint-disable-next-line import/no-cycle
import router from '@/router';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store';
import AppLoader from '@/components/AppLoader.vue';

export default { name: 'PollPage' };
</script>

<script setup lang="ts">
const ecoStore = useEcoHospitalStore();
const { poll } = storeToRefs(ecoStore);

const designList = ref<any[]>([]);

onMounted(async () => {
  await ecoStore.getPoll();
  designList.value = await ecoStore.getDesignList();
});

const step = ref(1);
const done = ref(false);
const isMobile = computed(() => window.innerWidth < 900);

const labDesigns = computed(() => designList.value.filter((d) => !!d.lab));
const regularDesigns = computed(() => designList.value.filter((d) => !d.lab));
const hasDesigns = computed(() => designList.value.length > 0);

const designStepNumber = computed(() => poll.value.length + 1);
const totalSteps = computed(() => poll.value.length + (hasDesigns.value ? 1 : 0));

const defineFieldType = computed(() => (type: string) => defineAsyncComponent(() => import(`~components/fields/${type}Field.vue`)));

const captureAnswer = (context:any) => {
  const qI = poll.value[step.value - 1].questions
    .findIndex((i) => i.number === context.number);

  if (qI > -1) poll.value[step.value - 1].questions[qI].answers = context.answers;
};

const prevStep = async (chapter:any) => {
  await ecoStore.sendChaptersAnswers({
    page: step.value,
    chapter,
  });

  step.value -= 1;
};

const nextStep = async (chapter:any) => {
  await ecoStore.sendChaptersAnswers({
    page: step.value,
    chapter,
  });

  step.value += 1;
};

const showFinishConfirm = ref(false);
const creatingPollFile = ref(false);

const continueLater = async () => {
  const lastChapter = poll.value[poll.value.length - 1];

  await ecoStore.sendChaptersAnswers({
    page: lastChapter.chapter_number,
    chapter: lastChapter,
  });

  goToMain();
};

const openFinishConfirm = () => {
  showFinishConfirm.value = true;
};

const confirmFinishPoll = async () => {
  creatingPollFile.value = true;

  try {
    const lastChapter = poll.value[poll.value.length - 1];

    await ecoStore.sendChaptersAnswers({
      page: lastChapter.chapter_number,
      chapter: lastChapter,
    });

    useMainStore().showMessage('Создаём документ. Появится в течение минуты в списке документов на подпись.');

    await ecoStore.createPollFile();

    showFinishConfirm.value = false;
    done.value = true;

    goToMain();
  } finally {
    creatingPollFile.value = false;
  }
};

const processingDesignIds = ref<number[]>([]);
const cancelingAllLab = computed(
  () => labDesigns.value.some((d) => processingDesignIds.value.includes(d.iddesign)),
);

const runCancelDesign = (iddesign: number, onSuccess: () => void) => {
  processingDesignIds.value.push(iddesign);

  ecoStore.cancelDesign(iddesign)
    .then(onSuccess)
    .catch(() => {})
    .finally(() => {
      processingDesignIds.value = processingDesignIds.value.filter((id) => id !== iddesign);
    });
};

const toggleDesign = (design: any) => {
  const { iddesign } = design;

  runCancelDesign(iddesign, () => {
    const target = designList.value.find((item) => item.iddesign === iddesign);
    if (target) target.canceled = !target.canceled;
  });
};

const cancelAllLabDesigns = () => {
  labDesigns.value
    .filter((d) => !d.canceled)
    .forEach((design) => {
      const { iddesign } = design;

      runCancelDesign(iddesign, () => {
        const target = designList.value.find((item) => item.iddesign === iddesign);
        if (target) target.canceled = true;
      });
    });
};
// function resetPoll() {
//   done.value = false;
//   step.value = 1;
// }

const goToMain = () => {
  router.push({
    name: 'HealthPageFull',
  });
};

</script>

<style>
.poll-page {

  min-height: 100%;
  padding-bottom: 50px;

  background: linear-gradient(
    180deg,
    #0b2d6b 0%,
    #144aa0 25%,
    #f6f7fb 65%,
    #f6f7fb 100%
  );

}

.poll-hero {

  padding: 24px 20px 26px;

}

.poll-hero-inner {

  max-width: 1150px;
  margin: 0 auto;

}

.poll-title {

  font-size: 22px;
  font-weight: 700;
  color: white;

}

.poll-subtitle {

  font-size: 14px;
  color: rgba(255,255,255,0.75);
  margin-top: 4px;

}

.poll-content {

  padding: 18px 20px 40px;

  max-width: 1150px;
  margin: 0 auto;

  background: white;

  border-radius: 26px;

  box-shadow: 0 -8px 30px rgba(0,0,0,0.08);

}

.poll-layout {

  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 32px;

}

.poll-sidebar {

  position: sticky;
  top: 70px;
  height: fit-content;

}

.chapter-item {

  display: flex;
  gap: 12px;

  padding: 12px;

  border-radius: 14px;

  border: 1px solid #eef0f6;

  cursor: pointer;

  transition: .15s;

  margin-bottom: 1rem;

}

.chapter-item:hover {

  background: #f6f8ff;

}

.chapter-item.active {

  border-color: #2563eb;
  background: #eef4ff;

}

.chapter-number {

  padding-left: 10px;
  padding-right: 10px;
  height: 30px;

  border-radius: 8px;

  background: #eef4ff;

  color: #2563eb;

  display: flex;
  align-items: center;
  justify-content: center;

  font-weight: 600;

}

.chapter-title {

  font-size: 14px;
  font-weight: 600;

}

.chapter-subtitle {

  font-size: 12px;
  color: #6b7280;

}

.poll-main {

  display: flex;
  flex-direction: column;

}

.chapter-header {

  font-size: 18px;
  font-weight: 700;

  margin-bottom: 16px;

}

.question-list {

  display: flex;
  flex-direction: column;
  gap: 14px;

}

.question-card {

  background: white;

  border: 1px solid #eef0f6;

  border-radius: 16px;

  padding: 16px;

}

/* Госуслуги: страница "Назначения" */

.designs-note {

  display: flex;
  align-items: flex-start;
  gap: 10px;

  background: #eef4ff;

  border-radius: 12px;

  padding: 12px 14px;

  margin-bottom: 16px;

  font-size: 13px;
  line-height: 1.5;
  color: #1f2937;

}

.designs-note .q-icon {
  margin: auto;
}
.designs-list {

  display: flex;
  flex-direction: column;
  gap: 12px;

}

.design-card {

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  background: white;

  border: 1px solid #eef0f6;

  border-radius: 14px;

  padding: 14px 16px;

}

.design-name {

  font-size: 14px;
  font-weight: 500;
  color: #111827;

}

.design-card--canceled {

  background: #f9fafb;

}

.design-card--canceled .design-name {

  color: #9ca3af;
  text-decoration: line-through;

}

.design-group {

  display: flex;
  flex-direction: column;
  gap: 12px;

  margin-top: 4px;

  padding-top: 14px;

  border-top: 1px dashed #e0e4ee;

}

.design-group-header {

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;

}

.design-group-title {

  font-size: 14px;
  font-weight: 700;
  color: #1f2937;

}

.gu-btn-sm {

  height: 36px;
  padding: 0 14px;

  font-size: 13px;

}

.poll-nav {

  display: flex;

  justify-content: space-between;

  margin-top: 26px;

}

.finish-card {

  border-radius: 18px;
  border: 1px solid #eef0f6;

}

/* Госуслуги: группа кнопок завершения анкетирования */

.gu-actions-right {

  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

}

.gu-btn {

  height: 48px;

  padding: 0 22px;

  border-radius: 8px;

  font-size: 15px;
  font-weight: 500;
  letter-spacing: normal;
  text-transform: none;

  transition: background-color .15s ease, border-color .15s ease;

}

.gu-btn-primary {

  background: #0d4cd3;
  color: #fff;

}

.gu-btn-primary:hover {
  background: #1d5deb;
  color: #fff;
}

.gu-btn-primary:active,
.gu-btn-primary:focus {
  background: #2a63ad;
}

.gu-btn-secondary {

  background: #fff;
  color: #0d4cd3;

  border: 2px solid #0d4cd3;

}

.gu-btn-secondary:hover {
  background: #e4ecfd;
}

.gu-btn-secondary:active,
.gu-btn-secondary:focus {
  background: #d3e0fb;
}

/* Госуслуги: конфирм-диалог завершения */

.gu-confirm-card {

  width: 400px;
  max-width: calc(100vw - 32px);

  padding: 32px 28px 28px;

  border-radius: 26px !important;
  overflow: hidden;

  box-shadow: 0 16px 40px rgba(13, 76, 211, 0.16);

}

/* скругление иконки задаётся отдельно и не зависит от радиуса .gu-confirm-card */
.gu-confirm-icon {

  width: 52px;
  height: 52px;
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 14px !important;

  background: #e4ecfd;
  color: #0d4cd3;

  margin-bottom: 16px;

}

.gu-confirm-title {

  font-size: 18px;
  font-weight: 700;
  color: #1f2937;

  margin-bottom: 8px;

}

.gu-confirm-text {

  font-size: 15px;
  line-height: 1.5;
  color: #4b5563;

  margin-bottom: 24px;

}

.gu-confirm-actions {

  display: flex;
  gap: 12px;

}

.gu-confirm-actions .gu-btn {
  flex: 1;
}

@media (max-width: 480px) {

  .gu-confirm-actions {
    flex-direction: column-reverse;
  }

}

.finish-title {

  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;

}

.finish-btn {

  height: 46px;
  border-radius: 10px;

}

.poll-field {

  display: flex;
  flex-direction: column;
  gap: 10px;

}

.poll-question {

  display: flex;
  align-items: flex-start;

  font-size: 15px;
  font-weight: 500;

  color: #111827;

}

.poll-number {

  background: #eef4ff;
  color: #2563eb;

  font-weight: 600;

}

.poll-question-text {

  line-height: 1.4;

}

.poll-options {

  display: flex;
  flex-direction: column;
  gap: 8px;

  margin-left: 34px;

}

.poll-input {

  max-width: 420px;

}

.question-number {
  min-width: 28px;
  height: 28px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 8px;

  background: #eef4ff !important;
  color: #2563eb !important;

  font-size: 13px;
  font-weight: 700;

  padding: 0;

  flex-shrink: 0;
}

.q-checkbox__label,
.q-radio__label {

  font-size: 14px;
  color: #374151;

}

.q-field--outlined .q-field__control {

  border-radius: 12px;
  background: #fafbff;
  border: 1px solid #e6e8ef;

}

.q-field--focused .q-field__control {

  border-color: #2563eb;

}

.poll-note {

  font-size: 12px;
  color: #6b7280;

  margin-left: 34px;

}

.q-checkbox,
.q-radio {

  padding: 6px 4px;
  border-radius: 8px;

}

.q-checkbox:hover,
.q-radio:hover {

  background: #f5f7ff;

}

@media (max-width: 900px) {

  .poll-layout {

    grid-template-columns: 1fr;

  }

  .poll-sidebar {

    flex-direction: row;
    overflow-x: auto;

  }

  .chapter-item {

    min-width: 180px;

  }

}

@media (max-width: 600px) {

  .poll-content {

    border-radius: 20px;

  }

}

.q-breadcrumbs {
  background-color: #eff5f9;
  padding: 0.5rem;
  border-radius: 0.5rem;
  margin-bottom: 15px;
}
</style>
